import React, { useState, useRef, useEffect } from 'react';

interface TooltipProps {
    text: string;
    content?: any;
    children: React.ReactNode;
}

const Tooltip: React.FC<TooltipProps> = ({ text, content, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isVisible && triggerRef.current && tooltipRef.current) {
            const triggerRect = triggerRef.current.getBoundingClientRect();
            const tooltipWidth = tooltipRef.current.offsetWidth;
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            let newTop = triggerRect.bottom + window.scrollY;
            let newLeft = triggerRect.left + (triggerRect.width - tooltipWidth) / 2 + window.scrollX;

            if (newLeft + tooltipWidth > viewportWidth) {
                newLeft = viewportWidth - tooltipWidth - 10;
            } else if (newLeft < 0) {
                newLeft = 10;
            }

            if (newTop + tooltipRef.current.offsetHeight > viewportHeight) {
                newTop = triggerRect.top + window.scrollY - tooltipRef.current.offsetHeight;
            }

            setPosition((prevPosition) => {
                if (prevPosition.top !== newTop || prevPosition.left !== newLeft) {
                    return { top: newTop, left: newLeft };
                }
                return prevPosition;
            });
        }
    }, [isVisible]);

    return (
        <>
            <div
                ref={triggerRef}
                onMouseEnter={() => setIsVisible(true)}
                onMouseLeave={() => setIsVisible(false)}
                className="tw-font-semibold tw-text-xl tw-text-[#091E42] tw-truncate tw-block"
            >
                {children}
            </div>
            {isVisible && (
                <div
                    ref={tooltipRef}
                    className={content != null ? "tw-fixed tw-z-50 tw-p-2" : "tw-fixed tw-z-50 tw-p-2 tw-bg-gray-800 tw-text-white tw-text-sm tw-rounded-md tw-shadow-lg"}
                    style={content != null ? {
                        top: `${position.top}px`,
                        left: `${position.left}px`,
                    } : {
                        top: `${position.top}px`,
                        left: `${position.left}px`,
                        maxWidth: '600px',
                        whiteSpace: 'pre-line',
                        wordWrap: 'break-word'
                    }}
                >
                    {content ?? text}
                </div>
            )}
        </>
    );
};

export default Tooltip;
